import { useState, useEffect, useCallback } from 'react';
import { fetchStockHistory, fetchStockHistoryStats, type StockHistoryEntry } from '@/lib/stockHistory';
import { supabase } from '@/lib/supabase';

const PAGE_SIZE = 30;

type FilterType = 'all' | 'deduct' | 'restore' | 'manual_update' | 'restock';
type DateFilter = 'today' | 'week' | 'month' | 'all';

const CHANGE_TYPE_CONFIG: Record<string, {
  label: string; icon: string; bg: string; border: string;
  badge: string; iconColor: string; amountColor: string; sign: string;
}> = {
  deduct: {
    label: 'Order Deduct',
    icon: 'ri-arrow-down-circle-fill',
    bg: 'bg-red-50',
    border: 'border-red-100',
    badge: 'bg-red-100 text-red-700 border-red-200',
    iconColor: 'text-red-500',
    amountColor: 'text-red-600',
    sign: '-',
  },
  restore: {
    label: 'Order Restore',
    icon: 'ri-arrow-up-circle-fill',
    bg: 'bg-green-50',
    border: 'border-green-100',
    badge: 'bg-green-100 text-green-700 border-green-200',
    iconColor: 'text-green-500',
    amountColor: 'text-green-600',
    sign: '+',
  },
  manual_update: {
    label: 'Manual Update',
    icon: 'ri-edit-box-fill',
    bg: 'bg-amber-50',
    border: 'border-amber-100',
    badge: 'bg-amber-100 text-amber-700 border-amber-200',
    iconColor: 'text-amber-500',
    amountColor: 'text-amber-600',
    sign: '→',
  },
  restock: {
    label: 'Restock',
    icon: 'ri-add-box-fill',
    bg: 'bg-emerald-50',
    border: 'border-emerald-100',
    badge: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    iconColor: 'text-emerald-500',
    amountColor: 'text-emerald-600',
    sign: '+',
  },
};

function getDateRange(filter: DateFilter): { from?: string; to?: string } {
  const now = new Date();
  if (filter === 'today') {
    const start = new Date(now); start.setHours(0, 0, 0, 0);
    return { from: start.toISOString() };
  }
  if (filter === 'week') {
    const start = new Date(now); start.setDate(now.getDate() - 7);
    return { from: start.toISOString() };
  }
  if (filter === 'month') {
    const start = new Date(now); start.setDate(now.getDate() - 30);
    return { from: start.toISOString() };
  }
  return {};
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffMin < 1) return 'Abhi abhi';
  if (diffMin < 60) return `${diffMin} min pehle`;
  if (diffHr < 24) return `${diffHr} ghante pehle`;
  if (diffDay === 1) return 'Kal';
  if (diffDay < 7) return `${diffDay} din pehle`;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

interface ProductOption { id: string; name: string; category: string }

export default function StockHistoryPanel() {
  const [entries, setEntries] = useState<StockHistoryEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [stats, setStats] = useState({ totalDeductions: 0, totalRestores: 0, totalManual: 0, totalRestocks: 0, todayChanges: 0 });
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [dateFilter, setDateFilter] = useState<DateFilter>('week');
  const [search, setSearch] = useState('');
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const loadProducts = useCallback(async () => {
    const { data } = await supabase
      .from('products')
      .select('id, name, category')
      .eq('status', 'approved')
      .order('name');
    setProducts((data ?? []) as ProductOption[]);
  }, []);

  const loadHistory = useCallback(async (reset = true) => {
    if (reset) { setLoading(true); setOffset(0); }
    else setLoadingMore(true);

    const range = getDateRange(dateFilter);
    const currentOffset = reset ? 0 : offset;

    const [histResult, statsResult] = await Promise.all([
      fetchStockHistory({
        productId: selectedProductId || undefined,
        changeType: filterType,
        limit: PAGE_SIZE,
        offset: currentOffset,
        from: range.from,
        to: range.to,
      }),
      reset ? fetchStockHistoryStats() : Promise.resolve(null),
    ]);

    if (reset) {
      setEntries(histResult.data);
      if (statsResult) setStats(statsResult);
    } else {
      setEntries(prev => [...prev, ...histResult.data]);
    }
    setTotal(histResult.total);
    setOffset(currentOffset + PAGE_SIZE);
    setLoading(false);
    setLoadingMore(false);
  }, [filterType, dateFilter, selectedProductId, offset]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    loadHistory(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, dateFilter, selectedProductId]);

  const filteredEntries = entries.filter(e => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      e.product_name.toLowerCase().includes(q) ||
      e.category?.toLowerCase().includes(q) ||
      e.order_id?.toLowerCase().includes(q) ||
      e.note?.toLowerCase().includes(q) ||
      e.variant_label?.toLowerCase().includes(q)
    );
  });

  const hasMore = entries.length < total;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/25">
              <i className="ri-history-line text-white text-xl"></i>
            </div>
            <div>
              <h2 className="text-gray-900 font-bold text-base">Stock History</h2>
              <p className="text-gray-400 text-xs">Har stock change ka complete log</p>
            </div>
          </div>
          <button
            onClick={() => loadHistory(true)}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 text-gray-600 text-sm font-semibold hover:bg-gray-200 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
            Refresh
          </button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <div className="bg-slate-50 border border-slate-100 rounded-xl p-3 text-center sm:col-span-1">
            <p className="text-slate-700 font-bold text-xl">{stats.todayChanges}</p>
            <p className="text-slate-500 text-[11px] font-semibold mt-0.5">Aaj ke changes</p>
          </div>
          <div className="bg-red-50 border border-red-100 rounded-xl p-3 text-center">
            <p className="text-red-600 font-bold text-xl">{stats.totalDeductions}</p>
            <p className="text-red-500 text-[11px] font-semibold mt-0.5">Deductions</p>
          </div>
          <div className="bg-green-50 border border-green-100 rounded-xl p-3 text-center">
            <p className="text-green-600 font-bold text-xl">{stats.totalRestores}</p>
            <p className="text-green-500 text-[11px] font-semibold mt-0.5">Restores</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-center">
            <p className="text-emerald-600 font-bold text-xl">{stats.totalRestocks}</p>
            <p className="text-emerald-500 text-[11px] font-semibold mt-0.5">Restocks</p>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-center">
            <p className="text-amber-600 font-bold text-xl">{stats.totalManual}</p>
            <p className="text-amber-500 text-[11px] font-semibold mt-0.5">Manual</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-3">
        {/* Date Filter */}
        <div className="grid grid-cols-4 gap-1.5">
          {([
            { key: 'today', label: 'Aaj', icon: 'ri-sun-line' },
            { key: 'week',  label: '7 Din', icon: 'ri-calendar-line' },
            { key: 'month', label: '30 Din', icon: 'ri-calendar-2-line' },
            { key: 'all',   label: 'Sab', icon: 'ri-history-line' },
          ] as const).map(tab => (
            <button
              key={tab.key}
              onClick={() => setDateFilter(tab.key)}
              className={`flex flex-col items-center gap-1 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                dateFilter === tab.key
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-50 text-gray-500 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <i className={`${tab.icon} text-sm`}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Type Filter */}
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {([
            { key: 'all',           label: 'Sab',          icon: 'ri-list-check' },
            { key: 'deduct',        label: 'Deduct',        icon: 'ri-arrow-down-circle-fill' },
            { key: 'restore',       label: 'Restore',       icon: 'ri-arrow-up-circle-fill' },
            { key: 'restock',       label: 'Restock',       icon: 'ri-add-box-fill' },
            { key: 'manual_update', label: 'Manual',        icon: 'ri-edit-box-fill' },
          ] as const).map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilterType(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all cursor-pointer shrink-0 ${
                filterType === tab.key
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
              }`}
            >
              <i className={tab.icon}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Product Filter */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by product, order ID..."
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-gray-400 focus:ring-2 focus:ring-gray-100 outline-none"
            />
          </div>
          <select
            value={selectedProductId}
            onChange={e => setSelectedProductId(e.target.value)}
            className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-700 focus:border-gray-400 outline-none bg-white cursor-pointer"
          >
            <option value="">All Products</option>
            {products.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-16 gap-4 bg-white rounded-2xl border border-gray-100">
          <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-indigo-500 animate-spin"></div>
          <p className="text-gray-500 text-sm">History load ho rahi hai...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredEntries.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 gap-4 bg-white rounded-2xl border border-gray-100">
          <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center">
            <i className="ri-history-line text-2xl text-gray-400"></i>
          </div>
          <div className="text-center">
            <p className="text-gray-700 font-semibold">Koi history nahi mili</p>
            <p className="text-gray-400 text-sm mt-1">
              {search ? 'Search se koi result nahi mila' : 'Is period mein koi stock change nahi hua'}
            </p>
          </div>
        </div>
      )}

      {/* History Entries */}
      {!loading && filteredEntries.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between px-1">
            <p className="text-gray-500 text-xs font-semibold">
              {filteredEntries.length} entries {search ? '(filtered)' : `of ${total} total`}
            </p>
            <p className="text-gray-400 text-xs">Newest first</p>
          </div>

          {filteredEntries.map(entry => {
            const config = CHANGE_TYPE_CONFIG[entry.change_type] ?? CHANGE_TYPE_CONFIG.manual_update;
            const isExpanded = expandedId === entry.id;

            return (
              <div
                key={entry.id}
                className={`rounded-2xl border overflow-hidden transition-all ${config.bg} ${config.border}`}
              >
                {/* Main Row */}
                <div
                  className="flex items-center gap-3 p-3.5 cursor-pointer"
                  onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                >
                  {/* Icon */}
                  <div className={`w-10 h-10 rounded-xl bg-white flex items-center justify-center shrink-0 border ${config.border}`}>
                    <i className={`${config.icon} text-lg ${config.iconColor}`}></i>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-gray-900 font-bold text-sm truncate">{entry.product_name}</p>
                      <span className={`shrink-0 text-sm font-bold ${config.amountColor}`}>
                        {config.sign}{Math.abs(entry.change_amount)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${config.badge}`}>
                        {config.label}
                      </span>
                      {entry.variant_label && (
                        <span className="text-gray-500 text-xs truncate max-w-[120px]">{entry.variant_label}</span>
                      )}
                      <span className="text-gray-400 text-xs ml-auto shrink-0">{formatTime(entry.created_at)}</span>
                    </div>
                    {/* Stock before → after */}
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className="text-gray-400 text-xs">{entry.stock_before}</span>
                      <i className="ri-arrow-right-line text-gray-300 text-xs"></i>
                      <span className={`text-xs font-bold ${entry.stock_after === 0 ? 'text-red-500' : entry.stock_after <= 5 ? 'text-orange-500' : 'text-gray-700'}`}>
                        {entry.stock_after}
                      </span>
                      <span className="text-gray-400 text-xs">units</span>
                      {entry.stock_after === 0 && (
                        <span className="px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 text-[9px] font-bold">OUT</span>
                      )}
                    </div>
                  </div>

                  <i className={`text-gray-400 text-base shrink-0 transition-transform ${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-white/60 space-y-2 pt-3">
                    <div className="bg-white rounded-xl border border-gray-100 divide-y divide-gray-50">
                      {[
                        { label: 'Product', value: entry.product_name, icon: 'ri-shopping-bag-line' },
                        { label: 'Category', value: entry.category || '—', icon: 'ri-folder-line' },
                        { label: 'Change Type', value: config.label, icon: 'ri-exchange-line' },
                        { label: 'Stock Before', value: `${entry.stock_before} units`, icon: 'ri-archive-line' },
                        { label: 'Stock After', value: `${entry.stock_after} units`, icon: 'ri-archive-fill' },
                        { label: 'Change', value: `${config.sign}${Math.abs(entry.change_amount)} units`, icon: 'ri-scales-line' },
                        ...(entry.variant_label ? [{ label: 'Variant', value: entry.variant_label, icon: 'ri-price-tag-3-line' }] : []),
                        ...(entry.order_id ? [{ label: 'Order ID', value: `#${entry.order_id.slice(-8).toUpperCase()}`, icon: 'ri-receipt-line' }] : []),
                        ...(entry.order_status ? [{ label: 'Order Status', value: entry.order_status, icon: 'ri-checkbox-circle-line' }] : []),
                        ...(entry.note ? [{ label: 'Note', value: entry.note, icon: 'ri-sticky-note-line' }] : []),
                        { label: 'By', value: entry.performed_by || 'admin', icon: 'ri-user-line' },
                        { label: 'Time', value: new Date(entry.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }), icon: 'ri-time-line' },
                      ].map((row, idx) => (
                        <div key={idx} className="flex items-start gap-3 px-3 py-2.5">
                          <i className={`${row.icon} text-gray-400 text-sm mt-0.5 shrink-0`}></i>
                          <span className="text-gray-500 text-xs w-24 shrink-0">{row.label}</span>
                          <span className="text-gray-800 text-xs font-semibold flex-1 break-all">{row.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Load More */}
          {hasMore && !search && (
            <div className="flex flex-col items-center gap-2 pt-2">
              <button
                onClick={() => loadHistory(false)}
                disabled={loadingMore}
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-white border border-gray-200 text-gray-700 text-sm font-semibold hover:border-gray-400 hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {loadingMore ? (
                  <><i className="ri-loader-4-line animate-spin"></i>Loading...</>
                ) : (
                  <><i className="ri-arrow-down-line"></i>Load More ({Math.min(PAGE_SIZE, total - entries.length)} more)</>
                )}
              </button>
              <p className="text-xs text-gray-400">{entries.length} of {total} loaded</p>
            </div>
          )}

          {!hasMore && entries.length > 0 && !search && (
            <p className="text-center text-xs text-gray-400 flex items-center justify-center gap-1 py-2">
              <i className="ri-check-line text-green-500"></i>
              Sab {total} entries load ho gayi
            </p>
          )}
        </div>
      )}

      {/* Legend */}
      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4">
        <p className="text-gray-700 font-bold text-sm mb-3 flex items-center gap-2">
          <i className="ri-information-line text-indigo-500"></i>
          Change Types Explained
        </p>
        <div className="space-y-2">
          {Object.entries(CHANGE_TYPE_CONFIG).map(([key, cfg]) => (
            <div key={key} className="flex items-center gap-3">
              <i className={`${cfg.icon} ${cfg.iconColor} text-base shrink-0`}></i>
              <div>
                <span className="text-gray-800 text-xs font-bold">{cfg.label}: </span>
                <span className="text-gray-500 text-xs">
                  {key === 'deduct' && 'Order confirm hone pe stock automatically ghata'}
                  {key === 'restore' && 'Order cancel/reject hone pe stock wapas aaya'}
                  {key === 'restock' && 'Stock Alerts panel se quick restock kiya'}
                  {key === 'manual_update' && 'Products panel se manually stock set kiya'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
